import React, { useCallback, useEffect } from 'react';
import propTypes from 'prop-types';
import clsx from 'clsx';
import { useStorageState } from 'just-storage-hooks';
import {
	makeStyles, Button, Checkbox, Avatar,
	List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction, ListSubheader,
} from '@material-ui/core';
import fetch from 'node-fetch';
import useFirebase, { refMerge, useFirestoreCollectionRT } from '../hooks/firebase';

const UPKEEP_DELAY = 30000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
	},
	header: {
		display: 'flex',
		flexDirection: 'row',
		background: '#fff',
	},
}));

function donationSort([, a], [, b]) {
	return b.createdDateUTC > a.createdDateUTC ? 1 : -1;
}

export default function AdminDonations(props) {
	const { className } = props;
	const classes = useStyles();

	const { firestore } = useFirebase();

	const [running, setRunning] = useStorageState('adminDonationsRunning', false);

	const teams = useFirestoreCollectionRT('teams');
	const donations = useFirestoreCollectionRT('donations');

	const refreshDonations = useCallback(async (startTime = 0) => {
		for (const teamId of Object.keys(teams || {})) {
			await delay(100);

			if (!teams[teamId].active) continue;

			const res = await fetch(`https://www.extra-life.org/api/teams/${teamId}/donations`);
			const list = await res.json();
			if (!list) continue;

			for (const donation of list) {
				if (new Date(donation.createdDateUTC).getTime() > startTime) {
					const ref = firestore.doc(`donations/${donation.donationID}`);
					await refMerge(ref, donation).catch(console.log);
				}
			}
		}
	}, [firestore, teams]);

	useEffect(() => {
		if (!running) return () => {};

		let startTime = 0;

		const upkeep = () => {
			const now = Date.now();
			refreshDonations(startTime);
			startTime = now - UPKEEP_DELAY * 10;
		};

		const timer = setInterval(upkeep, UPKEEP_DELAY);
		upkeep();

		return () => {
			clearInterval(timer);
		};
	}, [running, refreshDonations]);

	const setDonationHidden = async (donationId, value) => {
		const ref = firestore.doc(`donations/${donationId}`);
		await ref.set({ hidden: value }, { merge: true });
	};

	return (
		<List className={clsx(classes.root, className)} dense>
			<ListSubheader className={classes.header}>
				<span style={{ flex: '100% 1 1' }}>Donations</span>
				{!running ? (
					<Button
						variant="contained"
						color="primary"
						onClick={() => setRunning(true)}
					>
						Start
					</Button>
				) : (
					<Button
						variant="contained"
						color="secondary"
						onClick={() => setRunning(false)}
					>
						Stop
					</Button>
				)}
			</ListSubheader>
			{donations && Object.entries(donations).sort(donationSort).map(([donationId, donation]) => (
				<ListItem>
					<ListItemIcon>
						<Avatar src={donation.avatarImageURL} />
					</ListItemIcon>
					<ListItemText
						primary={`${donation.displayName || 'Anonymous Human'} ${new Date(donation.createdDateUTC).toLocaleString()}`}
						secondary={`$${donation.amount.toFixed(2)} => ${donation.recipientName} (${teams[donation.teamID]?.name?.slice(0, 19) || 'No Team'})`}
					/>
					<ListItemSecondaryAction>
						<Checkbox
							edge="end"
							checked={!donation.hidden}
							onChange={() => setDonationHidden(donationId, !donation.hidden)}
						/>
					</ListItemSecondaryAction>
				</ListItem>
			))}
		</List>
	);
}

AdminDonations.defaultProps = {
	className: undefined,
};

AdminDonations.propTypes = {
	className: propTypes.string,
};
